import { createContext, useContext } from 'react';

import type { AuthContextType } from './types';

/**
 * Authentication context
 * Provides auth state and methods to child components
 */
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

/**
 * Hook to access authentication state and methods
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, login, logout } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <button onClick={() => login()}>Login</button>;
 *   }
 *
 *   return <button onClick={logout}>Logout ({user?.profile.name})</button>;
 * }
 * ```
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
